import webstorage from 'webstorage';
import { isString, isFunction, findKey, includes, keys, reduce } from 'lodash';

import STORAGE_TYPES from 'commons-core-services/constants/storageTypes.js';
import WindowProxy from 'commons-core-services/services/WindowProxy/WindowProxy.js';

/* eslint-disable no-underscore-dangle */

// @todo maybe check for other form of storage (Cache, indexedDB ...)

/**
 * Service that handles various implementations of storage exposing an interface defined by the W3C.
 *
 * It wraps the {@link SessionStorage} and {@link LocalStorage} implementations of {@link WebStorage} in the browser.<br>
 * They use {@link WindowProxy} to access those globals in Window, avoiding breakage when doing SSR for example.<br>
 *
 * This service allows memory storage using npm package `webstorage`, which implements {@link WebStorage}.<br>
 * It is possible to extend the storage implementations by creating plugins for this npm package.<br>
 * See npm package `webstorage-local` https://github.com/tatsuyaoiw/webstorage-local for example.
 *
 * @class StorageService
 *
 * @see https://www.w3.org/TR/webstorage
 * @see https://developer.mozilla.org/en/docs/Web/API/Storage
 * @see https://www.npmjs.com/package/webstorage
 * @see https://github.com/tatsuyaoiw/webstorage-local
 *
 */
class StorageService {
    /**
     * Simple and unique constructor for {@link StorageService}.
     *
     * @memberof StorageService
     */
    constructor() {
        function _getStorageInstance(storage) {
            if (!isString(storage)) {
                return null;
            }

            const type = includes(keys(STORAGE_TYPES), storage) ? storage : '';

            switch (type) {
                case 'IN_MEMORY':
                    return webstorage();
                case 'LOCAL_STORAGE':
                    return WindowProxy.localStorage() || null;
                case 'SESSION_STORAGE':
                    return WindowProxy.sessionStorage() || null;
                default:
                    return null;
            }
        }

        const storageInstances = reduce(
            keys(STORAGE_TYPES),
            (reducer, value) => {
                reducer[value] = _getStorageInstance(value);
                return reducer;
            },
            {}
        );

        /**
         * @type {Object} instances of storage for the various types supported
         * @property {WebStorage} __instances.IN_MEMORY implementation of {@link WebStorage} into memory
         * @property {?WebStorage} __instances.LOCAL_STORAGE implementation {@link LocalStorage} of {@link WebStorage}, or {@link null}
         * @property {?WebStorage} __instances.SESSION_STORAGE implementation {@link SessionStorage} of {@link WebStorage}, or {@link null}
         */
        this.__instances = storageInstances;
    }

    /**
     * Provide access for the {@link WebStorage} matching with the given type.
     *
     * If the type doesn't exist, it returns {@link null}.<br>
     * Some types are available only in some specific environment (ie. {@link LocalStorage} and {@link SessionStorage} depends on window).
     *
     * For ease of use **type** supports multiple string variations, after applying lower casing.<br>
     * See {@link STORAGE_TYPES} as the valid values depending on storage type.
     *
     * @example <caption>When using 'localStorage'</caption>
     * const localStorage = StorageService.from('localStorage');    // using window.localStorage
     * localStorage.setItem('token', 'abc');                        // set 'token' to 'abc'
     * var variable = localStorage.getItem('abc');                  // variable equals 'abc'
     * localStorage.clear();                                        // reset all values from window.localStorage
     *
     * @example <caption>When using invalid storage type</caption>
     * var instance = StorageService.from('test');                  // instance equals null
     *
     * @memberof StorageService
     * @param {string} storage the type of {@link WebStorage} to return
     * @return {?WebStorage} returns the given type of {@link WebStorage}, or {@link null}
     * @throws {TypeError} throws an error when **storage** is not a {@link string}
     */
    from(storage) {
        if (!isString(storage)) {
            throw new TypeError('StorageService#from - [storage] must be a string');
        }

        const type = findKey(STORAGE_TYPES, (labels) => {
            return includes(labels, storage.toLocaleLowerCase());
        });

        return (!!type && this.__instances[type]) || null;
    }

    /**
     * Provide access for the {@link WebStorage} matching with the given type through a callback function.<br>
     * This callback exposes the storage by passing it as an argument.
     *
     * If the type doesn't exist, it doesn't execute the callback and returns {@link undefined}.<br>
     * This method calls internally {@link StorageService#from}, see this method for further explanation on storage type.
     *
     * The return value is either {@link undefined} or the return value from the callback, if any.
     *
     * The main advantage over {@link StorageService#from} is **it guarantees the existence of the storage** in the calling environment.<br>
     * Otherwise the callback is just ignored.
     *
     * @example <caption>When using memory storage</caption>
     * var variable = StorageService.using('memory', (storage) => {
     *      storage.setItem('test', 'toto');
     *      return storage.getItem('test');
     * });  // variable equals 'toto'
     *
     * @example <caption>When using invalid storage type</caption>
     * var variable = StorageService.using('test', function (storage) {
     *      storage.setItem('test', 'tata');
     *      return storage.getItem('test');
     * });  // variable equals undefined
     *
     * @memberof StorageService
     * @param {string} storage the type of {@link WebStorage} to use
     * @param {function(storage: WebStorage): ?any} callback the process to apply to the given storage
     * @return {?any} returns the value returned by the callback, if any, or {@link undefined}
     * @throws {TypeError} throws an error when **storage** is not a {@link string}
     * @throws {TypeError} throws an error when **callback** is not a {@link function}
     */
    using(storage, callback) {
        if (!isString(storage)) {
            throw new TypeError('StorageService#using - [storage] must be a string');
        }

        if (!isFunction(callback)) {
            throw new TypeError('StorageService#using - [callback] must be a function');
        }

        const store = this.from(storage);
        return store ? callback(store) : undefined;
    }
}

/**
 * Instance of {@link StorageService} to be exposed in bundle.
 */
export default new StorageService();
